import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, HStack, VStack } from '@chakra-ui/react';
import { ClearFiltersButton, MenuRow, SearchableCheckboxMenu } from '../../../ui';
import { filterLocations, useGetLocations } from '../../../hooks/useGetLocations';
import { useInvoicingSummary, useInvoicingTimeseries } from '../../../hooks/reporting';
import { INVOICING_COMPOSITION_METRICS, TOP_FLAGGED_VENDOR_TYPES_METRIC } from '../widgets/invoicingCatalog';
import { useWidgetDashboard } from '../widgets/useWidgetDashboard';
import WidgetGrid, { type WidgetGridHandle } from '../widgets/WidgetGrid';
import InvoicingWidgetRenderer, { type InvoicingWidgetDataContext } from '../widgets/InvoicingWidgetRenderer';
import {
    INVOICING_CATALOG,
    INVOICING_STORAGE_KEY,
    INVOICING_LAYOUT_VERSION,
    isRetiredInvoicingWidget,
    getDefaultInvoicingWidgets,
    sanitizeInvoicingWidget,
} from '../widgets/invoicingCatalog';
import { exportToCsv } from '../../../utils/csvExport';
import { invoicingCompositionCsvRows } from '../components/InvoicingCompositionDonut';
import { FLAG_TYPE_LABEL, FLAG_TYPE_ORDER } from '../components/InvoicingTimeseriesChart';
import type { WidgetSharedParams } from '../widgets/useWidgetData';
import { getReportingComparison } from '../shared/reportingComparisonUtils';
import type { FlagTypeKey, InvoiceTypeKey } from '../../../types/reporting';

const INVOICING_FILTERS_STORAGE_KEY = 'reporting_invoicing_filters';

const INVOICE_TYPE_SET = new Set<string>(['received', 'returned', 'return_slip', 'sublet']);

interface StoredInvoicingFilters {
    vendors: string[];
    invoiceTypes: InvoiceTypeKey[];
    locations: string[];
    flagTypes: FlagTypeKey[];
    flagMatch: 'any' | 'exact';
}

const EMPTY_INVOICING_FILTERS: StoredInvoicingFilters = {
    vendors: [],
    invoiceTypes: [],
    locations: [],
    flagTypes: [],
    flagMatch: 'any',
};

function uniqueStrings(value: unknown, allowBlank: boolean, caseSensitive = false): string[] {
    if (!Array.isArray(value)) return [];
    const names: string[] = [];
    const seen = new Set<string>();
    for (const entry of value) {
        if (typeof entry !== 'string') continue;
        if (!allowBlank && entry.length === 0) continue;
        const key = caseSensitive ? entry : entry.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        names.push(entry);
        if (names.length >= 200) break;
    }
    return names;
}

function loadInvoicingFilters(): StoredInvoicingFilters {
    try {
        const raw = localStorage.getItem(INVOICING_FILTERS_STORAGE_KEY);
        if (!raw) return EMPTY_INVOICING_FILTERS;
        const parsed = JSON.parse(raw) as Partial<StoredInvoicingFilters>;
        const flagTypes = uniqueStrings(parsed.flagTypes, false).filter((flag): flag is FlagTypeKey =>
            (FLAG_TYPE_ORDER as readonly string[]).includes(flag),
        );
        const invoiceTypes = uniqueStrings(parsed.invoiceTypes, false).filter((type): type is InvoiceTypeKey =>
            INVOICE_TYPE_SET.has(type),
        );
        return {
            vendors: uniqueStrings(parsed.vendors, true),
            invoiceTypes,
            locations: uniqueStrings(parsed.locations, false, true),
            flagTypes,
            flagMatch: parsed.flagMatch === 'exact' ? 'exact' : 'any',
        };
    } catch {
        return EMPTY_INVOICING_FILTERS;
    }
}

function saveInvoicingFilters(filters: StoredInvoicingFilters): void {
    try {
        localStorage.setItem(INVOICING_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch {
        // Ignore blocked storage.
    }
}

interface InvoicingTabProps {
    startDate: string;
    endDate: string;
    locationId?: string;
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    preset?: string;
    isActive?: boolean;
    onRegisterAddWidget?: (fn: () => void) => void;
}

/** Menu option value for blank `supplierText` — empty string is a bad React key. */
const NO_VENDOR_OPTION_VALUE = '__no_vendor__';

const vendorOptionLabel = (name: string) => name || 'No vendor';

const toVendorOptionValue = (name: string) => (name === '' ? NO_VENDOR_OPTION_VALUE : name);

const fromVendorOptionValue = (value: string) => (value === NO_VENDOR_OPTION_VALUE ? '' : value);

const INVOICE_TYPE_FILTER_OPTIONS: { value: InvoiceTypeKey; label: string }[] = [
    { value: 'received', label: 'Received' },
    { value: 'returned', label: 'Credited' },
    { value: 'return_slip', label: 'Return Slip' },
    { value: 'sublet', label: 'Sublet' },
];

const FLAG_TYPE_FILTER_OPTIONS = [...FLAG_TYPE_ORDER]
    .sort((a, b) => FLAG_TYPE_LABEL[a].localeCompare(FLAG_TYPE_LABEL[b], undefined, { sensitivity: 'base' }))
    .map((value) => ({
        value,
        label: FLAG_TYPE_LABEL[value],
    }));

const InvoicingTab = ({
    startDate,
    endDate,
    locationId,
    granularity = 'week',
    preset,
    isActive = true,
    onRegisterAddWidget,
}: InvoicingTabProps) => {
    const gridRef = useRef<WidgetGridHandle>(null);
    const [storedFilters] = useState(loadInvoicingFilters);
    const [selectedVendors, setSelectedVendors] = useState<string[]>(storedFilters.vendors);
    const [selectedInvoiceTypes, setSelectedInvoiceTypes] = useState<InvoiceTypeKey[]>(storedFilters.invoiceTypes);
    const [selectedLocations, setSelectedLocations] = useState<string[]>(storedFilters.locations);
    const [selectedFlagTypes, setSelectedFlagTypes] = useState<FlagTypeKey[]>(storedFilters.flagTypes);
    const [flagMatchMode, setFlagMatchMode] = useState<'any' | 'exact'>(storedFilters.flagMatch);
    const { data: locationsData } = useGetLocations();

    useEffect(() => {
        if (onRegisterAddWidget) {
            onRegisterAddWidget(() => gridRef.current?.openAddWidget());
        }
    }, [onRegisterAddWidget]);

    useEffect(() => {
        saveInvoicingFilters({
            vendors: selectedVendors,
            invoiceTypes: selectedInvoiceTypes,
            locations: selectedLocations,
            flagTypes: selectedFlagTypes,
            flagMatch: flagMatchMode,
        });
    }, [selectedVendors, selectedInvoiceTypes, selectedLocations, selectedFlagTypes, flagMatchMode]);

    const comparison = getReportingComparison(preset);
    const vendorNames = selectedVendors.length > 0 ? selectedVendors : undefined;
    const invoiceTypes = selectedInvoiceTypes.length > 0 ? selectedInvoiceTypes : undefined;
    const locationIds = !locationId && selectedLocations.length > 0 ? selectedLocations : undefined;
    const flagTypes = selectedFlagTypes.length > 0 ? selectedFlagTypes : undefined;
    const flagMatch = flagTypes && flagMatchMode === 'exact' ? 'exact' as const : undefined;
    const hasInvoicingFilters = selectedVendors.length > 0
        || selectedInvoiceTypes.length > 0
        || selectedFlagTypes.length > 0
        || (!locationId && selectedLocations.length > 0);

    const clearInvoicingFilters = () => {
        setSelectedVendors(EMPTY_INVOICING_FILTERS.vendors);
        setSelectedInvoiceTypes(EMPTY_INVOICING_FILTERS.invoiceTypes);
        setSelectedLocations(EMPTY_INVOICING_FILTERS.locations);
        setSelectedFlagTypes(EMPTY_INVOICING_FILTERS.flagTypes);
        setFlagMatchMode(EMPTY_INVOICING_FILTERS.flagMatch);
    };

    const { data: summaryData, isFetching: summaryFetching, isError: summaryError } = useInvoicingSummary(
        startDate, endDate, locationId, preset, comparison, isActive, vendorNames, invoiceTypes, locationIds, flagTypes, flagMatch
    );
    const { data: timeseriesData, isFetching: timeseriesFetching } = useInvoicingTimeseries(
        startDate, endDate, granularity, locationId, preset, comparison, isActive, vendorNames, invoiceTypes, locationIds, flagTypes, flagMatch
    );

    const vendorOptionsForSelector = useMemo(() => {
        const map = new Map<string, { value: string; label: string }>();
        for (const name of summaryData?.vendorOptions ?? []) {
            map.set(name, { value: toVendorOptionValue(name), label: vendorOptionLabel(name) });
        }
        for (const name of selectedVendors) {
            if (!map.has(name)) {
                map.set(name, { value: toVendorOptionValue(name), label: vendorOptionLabel(name) });
            }
        }
        return Array.from(map.values());
    }, [summaryData?.vendorOptions, selectedVendors]);

    const selectedVendorOptionValues = useMemo(
        () => selectedVendors.map(toVendorOptionValue),
        [selectedVendors],
    );

    const locationOptions = useMemo(() => {
        const locations = filterLocations(locationsData?.locations ?? [], { excludeAll: true, activeOnly: true });
        return locations
            .filter((location) => location._id !== 'all')
            .map((location) => ({ value: location._id, label: location.title }));
    }, [locationsData?.locations]);

    const { widgets, addWidget, updateWidget, removeWidget, reorderWidgets } = useWidgetDashboard(false, {
        storageKey: INVOICING_STORAGE_KEY,
        layoutVersion: INVOICING_LAYOUT_VERSION,
        getDefaults: getDefaultInvoicingWidgets,
        sanitizeWidget: sanitizeInvoicingWidget,
        dropWidget: isRetiredInvoicingWidget,
    });

    const widgetData: InvoicingWidgetDataContext = {
        summary: summaryData,
        summaryLoading: summaryFetching && !summaryData,
        summaryError: !!summaryError,
        timeseries: timeseriesData,
        timeseriesLoading: timeseriesFetching && !timeseriesData,
        comparison,
        granularity,
        preset,
        startDate,
        endDate,
        invoiceTypes,
        locationId,
        locationIds,
        vendorNames,
        flagTypes,
        flagMatch,
    };

    const sharedParams: WidgetSharedParams = {
        startDate,
        endDate,
        locationId,
        granularity,
        preset,
        comparison,
    };

    const getWidgetRefetching = (config: typeof widgets[number]) => {
        if (config.widgetType === 'keyMetric') {
            return summaryFetching && !!summaryData;
        }
        if (config.widgetType === 'timeSeries') {
            if (INVOICING_COMPOSITION_METRICS.has(config.metric) || config.metric === TOP_FLAGGED_VENDOR_TYPES_METRIC) {
                return summaryFetching && !!summaryData;
            }
            return timeseriesFetching && !!timeseriesData;
        }
        return false;
    };

    return (
        <VStack spacing={4} align="stretch">
            <HStack spacing={3} wrap="wrap">
                {!locationId && (
                    <SearchableCheckboxMenu
                        options={locationOptions}
                        value={selectedLocations}
                        onChange={setSelectedLocations}
                        pinnedAllLabel="All Locations"
                        emptyTriggerLabel="All Locations"
                        searchPlaceholder="Search locations..."
                        width="220px"
                    />
                )}
                <SearchableCheckboxMenu
                    options={vendorOptionsForSelector}
                    value={selectedVendorOptionValues}
                    onChange={(values) => setSelectedVendors(values.map(fromVendorOptionValue))}
                    pinnedAllLabel="All Vendors"
                    emptyTriggerLabel="All Vendors"
                    searchPlaceholder="Search vendors..."
                    width="220px"
                />
                <SearchableCheckboxMenu
                    options={INVOICE_TYPE_FILTER_OPTIONS}
                    value={selectedInvoiceTypes}
                    onChange={(values) => setSelectedInvoiceTypes(values as InvoiceTypeKey[])}
                    pinnedAllLabel="All Invoice Types"
                    emptyTriggerLabel="All Invoice Types"
                    searchPlaceholder="Search invoice types..."
                    width="220px"
                />
                <SearchableCheckboxMenu
                    options={FLAG_TYPE_FILTER_OPTIONS}
                    value={selectedFlagTypes}
                    onChange={(values) => setSelectedFlagTypes(values as FlagTypeKey[])}
                    pinnedAllLabel="All Flag Types"
                    emptyTriggerLabel="All Flag Types"
                    searchPlaceholder="Search flag types..."
                    width="240px"
                    triggerPrefix={flagMatch === 'exact' ? 'Only' : undefined}
                    belowSearch={(
                        <Box px="6px" pt="6px" borderBottom="1px solid" borderColor="gray.100">
                            <MenuRow
                                kind="single"
                                isSelected={flagMatchMode !== 'exact'}
                                onClick={() => setFlagMatchMode('any')}
                            >
                                At least one of these
                            </MenuRow>
                            <MenuRow
                                kind="single"
                                isSelected={flagMatchMode === 'exact'}
                                onClick={() => setFlagMatchMode('exact')}
                            >
                                Only this combination
                            </MenuRow>
                        </Box>
                    )}
                />
                {hasInvoicingFilters && (
                    <ClearFiltersButton onClick={clearInvoicingFilters} />
                )}
            </HStack>
            <WidgetGrid
                ref={gridRef}
                widgets={widgets.map(sanitizeInvoicingWidget)}
                params={sharedParams}
                onUpdate={updateWidget}
                onRemove={removeWidget}
                onAdd={addWidget}
                onReorder={reorderWidgets}
                isGm={false}
                editorCatalog={INVOICING_CATALOG}
                renderWidget={(config) => (
                    <InvoicingWidgetRenderer
                        config={config}
                        data={widgetData}
                        onSortChange={(sort) => updateWidget(config.id, { sort })}
                    />
                )}
                getWidgetRefetching={getWidgetRefetching}
                onDownloadWidget={(config) => {
                    const filename = config.title.replace(/\s+/g, '_').toLowerCase();
                    if (INVOICING_COMPOSITION_METRICS.has(config.metric)) {
                        const rows = invoicingCompositionCsvRows(config.metric, summaryData?.composition);
                        if (rows.length > 0) exportToCsv(rows, filename);
                        return;
                    }
                    if (config.metric === TOP_FLAGGED_VENDOR_TYPES_METRIC) {
                        const rows = (summaryData?.topFlaggedVendorTypes ?? []).map(({ flagRate: _flagRate, ...row }) => row);
                        if (rows.length > 0) exportToCsv(rows, filename);
                        return;
                    }
                    if (config.dataSource === 'timeseries' && timeseriesData) {
                        exportToCsv(timeseriesData.data, filename);
                    } else if (config.dataSource === 'summary' && summaryData) {
                        const { vendorOptions: _vendorOptions, composition: _composition, topFlaggedVendorTypes: _topFlaggedVendorTypes, topFlaggedVendorTypesHasMore: _topFlaggedVendorTypesHasMore, ...summaryForExport } = summaryData;
                        exportToCsv([summaryForExport], filename);
                    }
                }}
            />
        </VStack>
    );
};

export default InvoicingTab;
